// Nebim içe aktarma — Supabase operasyonları.
// RLS: tüm sorgular kullanıcının JWT'siyle çalışır; service_role KULLANILMAZ.

import type { SupabaseClient } from "@supabase/supabase-js";
import type { CommitResult, ParseResult } from "./types";
import type { StockParseResult } from "./parseStock";

const BATCH_TABLE = "import_batches";
const ROWS_TABLE = "import_rows";
const ISSUES_TABLE = "data_issues";
const SOURCE_SYSTEM = "nebim";
const IMPORT_TYPE = "nebim_sales_daily";

/**
 * Stage large Nebim files in bounded RPC payloads. Each chunk is transactional;
 * the batch remains in parsing state until the final chunk, preventing a timeout
 * from a single oversized JSON request. If a chunk fails, callers mark the batch
 * failed and reports continue to ignore it.
 */
async function commitStagingInChunks(
  client: SupabaseClient,
  companyId: string,
  batchId: string,
  rows: Array<Record<string, unknown>>,
  issues: Array<Record<string, unknown>>,
): Promise<{ error: { code?: string; message: string } | null }> {
  const CHUNK_SIZE = 400;
  for (let start = 0; start < rows.length; start += CHUNK_SIZE) {
    const chunk = rows.slice(start, start + CHUNK_SIZE);
    const rowNumbers = new Set(chunk.map((row) => Number(row.source_row_number)));
    const chunkIssues = issues.filter((issue) => {
      const details = issue.details;
      if (!details || typeof details !== "object") return false;
      const rowNumber = Number((details as Record<string, unknown>).source_row_number);
      return rowNumbers.has(rowNumber);
    });
    const isFinal = start + chunk.length >= rows.length;
    const { error } = await client.rpc("commit_nebim_staging_chunk", {
      p_company_id: companyId,
      p_batch_id: batchId,
      p_rows: chunk,
      p_issues: chunkIssues,
      p_finalize: isFinal,
    });
    if (error) return { error: { code: error.code, message: error.message } };
  }
  return { error: null };
}


/**
 * PostgREST can return no error when an UPDATE matches zero rows (for example,
 * because an RLS policy filters the row). Select the updated id so a status
 * transition is only treated as successful when exactly one batch was updated.
 */
async function transitionBatchStatus(
  client: SupabaseClient,
  batchId: string,
  companyId: string,
  status: string,
): Promise<{ code: string; message: string } | null> {
  const { data, error } = await client
    .from(BATCH_TABLE)
    .update({ status })
    .eq("id", batchId)
    .eq("company_id", companyId)
    .select("id")
    .maybeSingle();

  if (error) return { code: error.code ?? "update_failed", message: error.message };
  if (!data?.id) {
    return {
      code: "batch_status_not_updated",
      message: `Aktarım durumu "${status}" olarak güncellenemedi; kayıt bulunamadı veya RLS izni engelledi.`,
    };
  }
  return null;
}

/** Aynı dosyanın SHA-256 özetiyle daha önce yüklenip yüklenmediğini kontrol eder. */
export async function findDuplicateBatch(
  client: SupabaseClient,
  companyId: string,
  fileHash: string,
) {
  // A failed batch may already contain partially inserted rows because
  // REST writes across tables are not transactional. Treat it as a duplicate
  // until the partial batch is inspected/cleaned; otherwise retrying the same
  // file can create duplicate staging rows.
  const { data, error } = await client
    .from(BATCH_TABLE)
    .select("id, status, original_filename, created_at")
    .eq("company_id", companyId)
    .eq("file_sha256", fileHash)
    .neq("status", "superseded")
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  return { data, error };
}

/**
 * Onaylanan dosyayı mevcut Supabase staging şemasına kaydeder.
 * REST üzerinden birden fazla tabloya yazım tek transaction değildir.
 * Bir aşama başarısız olursa batch failed yapılır; önceki aşamada yazılmış
 * satırlar otomatik silinmez ve güvenli biçimde tekrar gözden geçirilmelidir.
 */

type CanonicalizationResult = NonNullable<Extract<CommitResult, { ok: true }>["canonicalization"]>;

/** Materialize committed staging rows idempotently into canonical reporting tables. */
async function materializeNebimBatch(
  client: SupabaseClient,
  companyId: string,
  batchId: string,
  snapshotDate: string | null,
): Promise<CanonicalizationResult> {
  const { data, error } = await client.rpc("materialize_nebim_batch_canonical", {
    p_company_id: companyId,
    p_batch_id: batchId,
    p_snapshot_date: snapshotDate,
  });

  if (error) {
    // Staging has already committed. Report the canonical write separately so the
    // UI never labels it as a full success, and an operator can safely retry later.
    return {
      materialized: false,
      mode: "staging_only",
      message: `Staging kaydedildi ancak kanonik satış/stok tablolarına işleme başarısız: ${error.message}`,
    };
  }

  const result = (data ?? {}) as Record<string, unknown>;
  return {
    materialized: result.materialized === true,
    mode: typeof result.mode === "string" ? result.mode : "staging_only",
    message: typeof result.message === "string" ? result.message : undefined,
    source_date: typeof result.source_date === "string" ? result.source_date : undefined,
    source_period_start: typeof result.source_period_start === "string" ? result.source_period_start : undefined,
    source_period_end: typeof result.source_period_end === "string" ? result.source_period_end : undefined,
    canonical_rows: typeof result.canonical_rows === "number" ? result.canonical_rows : undefined,
    inserted_rows: typeof result.inserted_rows === "number" ? result.inserted_rows : undefined,
    snapshot_id: typeof result.snapshot_id === "string" ? result.snapshot_id : undefined,
    location_mapping_required: result.location_mapping_required === true,
  };
}

export async function commitNebimBatch(
  client: SupabaseClient,
  companyId: string,
  userId: string,
  parse: ParseResult,
): Promise<CommitResult> {
  if (parse.missing_required.length > 0) {
    return {
      ok: false,
      stage: "batch",
      code: "sales_columns_missing",
      message: `Zorunlu satış alanları bulunamadı: ${parse.missing_required.join(", ")}`,
    };
  }
  if (parse.rows.length === 0) {
    return {
      ok: false,
      stage: "batch",
      code: "sales_rows_empty",
      message: "Dosyada kaydedilebilir ürün satırı bulunamadı; boş aktarım oluşturulmadı.",
    };
  }

  if (parse.source_period_start && parse.source_period_end && parse.source_period_end < parse.source_period_start) {
    return { ok: false, stage: "batch", code: "invalid_source_period", message: "Nebim raporunun bitiş tarihi başlangıç tarihinden önce. Dosya kaydedilmedi." };
  }

  // Recheck at the service boundary so alternate callers cannot skip the UI guard.
  const duplicate = await findDuplicateBatch(client, companyId, parse.file_hash);
  if (duplicate.error) {
    return { ok: false, stage: "batch", code: duplicate.error.code ?? "duplicate_check_failed", message: `Mükerrer dosya kontrolü yapılamadı: ${duplicate.error.message}` };
  }
  if (duplicate.data) {
    return { ok: false, stage: "batch", code: "duplicate_file", message: `Bu dosya daha önce aktarılmış (${duplicate.data.status}); aynı dosya tekrar kaydedilmedi.` };
  }

  const { data: batch, error: bErr } = await client
    .from(BATCH_TABLE)
    .insert({
      company_id: companyId,
      source_system: SOURCE_SYSTEM,
      import_type: IMPORT_TYPE,
      original_filename: parse.file_name,
      file_sha256: parse.file_hash,
      source_period_start: parse.source_period_start ?? null,
      source_period_end: parse.source_period_end ?? null,
      // RLS permits staging rows while the batch is in parsing.
      // Finalization below follows the database-enforced status lifecycle.
      status: "parsing",
      total_rows: parse.row_count,
      parsed_rows: parse.rows.length,
      valid_rows: parse.valid_count,
      warning_rows: parse.warn_count,
      error_rows: parse.error_count,
      duplicate_rows: 0,
      unmatched_rows: 0,
      uploaded_by: userId,
      parser_version: "nebim-product-sales-v1",
    })
    .select("id")
    .single();

  if (bErr || !batch) {
    return {
      ok: false,
      stage: "batch",
      code: bErr?.code ?? "insert_failed",
      message: bErr?.message ?? "import_batches kaydı oluşturulamadı.",
    };
  }

  const markFailed = async (reason: string): Promise<string | null> => {
    // error_summary gerçek şemada jsonb'dir. Verify the failure state was saved;
    // otherwise the UI must not imply the batch was safely marked failed.
    const { data, error } = await client
      .from(BATCH_TABLE)
      .update({ status: "failed", error_summary: { message: reason.slice(0, 4000) } })
      .eq("id", batch.id)
      .eq("company_id", companyId)
      .select("id")
      .maybeSingle();
    if (error) return error.message;
    if (!data?.id) return "Aktarım kaydı failed durumuna geçirilemedi; RLS veya durum geçiş kuralı engellemiş olabilir.";
    return null;
  };

  const now = new Date().toISOString();
  const rowsPayload = parse.rows.map((r) => ({
    company_id: companyId,
    batch_id: batch.id,
    source_row_number: r.row_index,
    row_type: "data",
    row_status: r.status === "error" ? "error" : r.status === "warn" ? "warning" : "valid",
    raw_payload: r.raw,
    normalized_payload: r.parsed,
    source_date: parse.source_period_start && parse.source_period_start === parse.source_period_end ? parse.source_period_start : null,
    source_product_code: r.parsed.sku,
    source_barcode: r.parsed.barcode,
    parser_error_code: r.issues.find((i) => i.severity === "error")?.code ?? null,
    parser_error_message: r.issues.filter((i) => i.severity === "error").map((i) => i.message).join("; ") || null,
    parsed_at: now,
    validated_at: now,
  }));

  const issuesPayload = parse.rows.flatMap((r) =>
    r.issues.map((issue) => ({
      severity: issue.severity,
      issue_type: issue.severity === "error" ? "validation_error" : "warning",
      issue_code: issue.code,
      title: issue.severity === "error" ? "Satır doğrulama hatası" : "Satır doğrulama uyarısı",
      message: issue.message.trim() || issue.code,
      details: { source_row_number: r.row_index },
    })),
  );

  // Bounded chunks avoid statement timeouts for large stock/sales workbooks.
  const { error: atomicError } = await commitStagingInChunks(
    client, companyId, batch.id, rowsPayload, issuesPayload,
  );
  if (atomicError) {
    const failureStateError = await markFailed(atomicError.message);
    return {
      ok: false,
      stage: "rows",
      code: atomicError.code ?? "chunked_staging_failed",
      message: failureStateError
        ? `${atomicError.message} Ayrıca batch hata durumu kaydedilemedi: ${failureStateError}`
        : atomicError.message,
    };
  }

  const canonicalization = await materializeNebimBatch(client, companyId, batch.id, null);
  return {
    ok: true,
    batch_id: batch.id,
    row_count: parse.row_count,
    valid_count: parse.valid_count,
    error_count: parse.error_count,
    canonicalization,
  };
}

/** Nebim ürün kodlarını mevcut products.product_code alanıyla eşleştirir. */
export async function findUnmatchedSkus(
  client: SupabaseClient,
  companyId: string,
  skus: string[],
): Promise<{ unmatched: string[]; checked: boolean; error?: string }> {
  const unique = Array.from(new Set(skus.map((sku) => sku.trim()).filter(Boolean)));
  if (unique.length === 0) return { unmatched: [], checked: true };

  // Avoid oversized PostgREST URLs when a report contains many distinct SKUs.
  // Chunking also keeps the matching check deterministic for large inventory files.
  const found = new Set<string>();
  for (let i = 0; i < unique.length; i += 500) {
    const { data, error } = await client
      .from("products")
      .select("product_code")
      .eq("company_id", companyId)
      .in("product_code", unique.slice(i, i + 500));

    if (error) return { unmatched: [], checked: false, error: error.message };
    for (const product of data ?? []) {
      if (typeof product.product_code === "string") found.add(product.product_code);
    }
  }

  if (found.size === 0) {
    // The SKU-filtered query returns zero rows both when the catalogue is empty
    // and when it exists but none of these source SKUs match. Distinguish those
    // cases before reporting that matching could not be checked.
    const { data: anyProduct, error: catalogError } = await client
      .from("products")
      .select("product_code")
      .eq("company_id", companyId)
      .limit(1)
      .maybeSingle();

    if (catalogError) {
      return { unmatched: [], checked: false, error: catalogError.message };
    }
    if (!anyProduct) {
      return {
        unmatched: [],
        checked: false,
        error: "Şirketin ürün kataloğu boş; SKU eşleşmesi doğrulanamadı.",
      };
    }
  }

  return {
    unmatched: unique.filter((sku) => !found.has(sku)),
    checked: true,
  };
}


/** Nebim stok anlık görüntüsünü staging tablolarına kaydeder. */
export async function commitNebimStockBatch(
  client: SupabaseClient,
  companyId: string,
  userId: string,
  parse: StockParseResult,
  snapshotDate: string | null,
): Promise<CommitResult> {
  const parsedSnapshotDate = snapshotDate ? new Date(`${snapshotDate}T00:00:00Z`) : null;
  const validSnapshotDate = Boolean(
    snapshotDate &&
    /^\d{4}-\d{2}-\d{2}$/.test(snapshotDate) &&
    parsedSnapshotDate &&
    Number.isFinite(parsedSnapshotDate.getTime()) &&
    parsedSnapshotDate.toISOString().slice(0, 10) === snapshotDate
  );
  if (!validSnapshotDate) {
    return {
      ok: false,
      stage: "batch",
      code: "stock_snapshot_date_required",
      message: "Stok dosyasında güvenilir tarih yok. Gerçek stok görüntüsünün tarihini seçmeden kaydetme yapılamaz.",
    };
  }
  if (parse.missing_required.length > 0) {
    return {
      ok: false,
      stage: "batch",
      code: "stock_columns_missing",
      message: `Zorunlu stok alanları bulunamadı: ${parse.missing_required.join(", ")}`,
    };
  }
  if (parse.rows.length === 0) {
    return {
      ok: false,
      stage: "batch",
      code: "stock_rows_empty",
      message: "Dosyada kaydedilebilir stok satırı bulunamadı; boş aktarım oluşturulmadı.",
    };
  }

  // Recheck at the service boundary so alternate callers cannot skip the UI guard.
  const duplicate = await findDuplicateBatch(client, companyId, parse.file_hash);
  if (duplicate.error) {
    return { ok: false, stage: "batch", code: duplicate.error.code ?? "duplicate_check_failed", message: `Mükerrer dosya kontrolü yapılamadı: ${duplicate.error.message}` };
  }
  if (duplicate.data) {
    return { ok: false, stage: "batch", code: "duplicate_file", message: `Bu dosya daha önce aktarılmış (${duplicate.data.status}); aynı dosya tekrar kaydedilmedi.` };
  }

  const { data: batch, error: batchError } = await client
    .from(BATCH_TABLE)
    .insert({
      company_id: companyId,
      source_system: SOURCE_SYSTEM,
      import_type: "nebim_stock_snapshot",
      original_filename: parse.file_name,
      file_sha256: parse.file_hash,
      source_period_start: snapshotDate,
      source_period_end: snapshotDate,
      source_location_label: parse.warehouses.length === 1 ? parse.warehouses[0] : parse.warehouses.length > 1 ? "Çoklu kaynak konum" : null,
      status: "parsing",
      total_rows: parse.row_count,
      parsed_rows: parse.rows.length,
      valid_rows: parse.valid_count,
      warning_rows: parse.warn_count,
      error_rows: parse.error_count,
      duplicate_rows: 0,
      unmatched_rows: 0,
      uploaded_by: userId,
      parser_version: "nebim-stock-v1",
      error_summary: { warehouses: parse.warehouses },
    })
    .select("id")
    .single();

  if (batchError || !batch) {
    return {
      ok: false,
      stage: "batch",
      code: batchError?.code ?? "insert_failed",
      message: batchError?.message ?? "Stok import batch kaydı oluşturulamadı.",
    };
  }

  const markFailed = async (reason: string): Promise<string | null> => {
    const { data, error } = await client
      .from(BATCH_TABLE)
      .update({ status: "failed", error_summary: { message: reason.slice(0, 4000) } })
      .eq("id", batch.id)
      .eq("company_id", companyId)
      .select("id")
      .maybeSingle();
    if (error) return error.message;
    if (!data?.id) return "Stok aktarım kaydı failed durumuna geçirilemedi; RLS veya durum geçiş kuralı engellemiş olabilir.";
    return null;
  };

  const now = new Date().toISOString();
  const payload = parse.rows.map((row) => ({
    company_id: companyId,
    batch_id: batch.id,
    source_row_number: row.row_index,
    row_type: "data",
    row_status: row.status === "error" ? "error" : row.status === "warn" ? "warning" : "valid",
    raw_payload: row.raw,
    normalized_payload: row.parsed,
    source_product_code: row.parsed.sku,
    source_barcode: row.parsed.barcode,
    parser_error_code: row.issues.find((issue) => issue.severity === "error")?.code ?? null,
    parser_error_message: row.issues.filter((issue) => issue.severity === "error").map((issue) => issue.message).join("; ") || null,
    parsed_at: now,
    validated_at: now,
  }));

  const issuePayload = parse.rows.flatMap((row) => row.issues.map((issue) => ({
    severity: issue.severity,
    issue_type: issue.severity === "error" ? "validation_error" : "warning",
    issue_code: issue.code,
    title: issue.severity === "error" ? "Stok satırı doğrulama hatası" : "Stok satırı uyarısı",
    message: issue.message.trim() || issue.code,
    details: { source_row_number: row.row_index },
  })));

  // Bounded chunks avoid statement timeouts for large stock/sales workbooks.
  const { error: atomicError } = await commitStagingInChunks(
    client, companyId, batch.id, payload, issuePayload,
  );
  if (atomicError) {
    const failureStateError = await markFailed(atomicError.message);
    return {
      ok: false,
      stage: "rows",
      code: atomicError.code ?? "chunked_staging_failed",
      message: failureStateError
        ? `${atomicError.message} Ayrıca batch hata durumu kaydedilemedi: ${failureStateError}`
        : atomicError.message,
    };
  }

  const canonicalization = await materializeNebimBatch(client, companyId, batch.id, snapshotDate);
  return {
    ok: true,
    batch_id: batch.id,
    row_count: parse.row_count,
    valid_count: parse.valid_count,
    error_count: parse.error_count,
    canonicalization,
  };
}
